import { base64URLDecode, base64URLEncode, base64URLEncodeString } from '@/utils/base64-util';
import { generatePKCEParams } from './pkce-util';
import { authenticatedRoutes } from '@/utils/navigation-service';

/**
 * Key used to store the URL the login session was initiated from.
 * @type {string}
 * @constant
 */
const loginOriginKey = 'login_origin_url';

/**
 * OAuthCode provider for handling OAuth 2.0 authorization code flow with PKCE and state management.
 * This module manages the initiation of the OAuth flow, handling the callback,
 * exchanging the authorization code for tokens, and ensuring security via state and PKCE.
 */
export const OAuthCode = {
	/**
	 * Generate a cryptographically secure nonce for state parameter
	 * @private
	 *
	 * @returns {string} Random nonce string
	 */
	_generateNonce() {
		const array = new Uint8Array(16);
		crypto.getRandomValues(array);
		return base64URLEncode(array);
	},
	/**
	 * Compute redirect URI for route name
	 * @private
	 */
	_makeRedirectUri() {
		const redirectUrl = new URL(`/auth/${authenticatedRoutes.login}`, process.env.GATSBY_DOMAIN_URL);
		return redirectUrl.toString();
	},
	/**
	 * Initiates the OAuth authentication flow. Generates PKCE parameters, constructs the authorization URL,
	 * and stores necessary data in sessionStorage.
	 * @param {string} locale - User's locale for localization
	 * @returns {Promise<string>} - Authorization URL to redirect the user to
	 * @throws {Error} If the initiation of the OAuth flow fails
	 */
	initiateAuthentication: async locale => {
		try {
			const { codeVerifier, codeChallenge } = await generatePKCEParams();
			const nonce = OAuthCode._generateNonce();
			const loginOriginUrl = window.location.pathname;
			const state = {
				nonce,
				locale,
				loginOriginUrl,
			};

			// Store PKCE verifier and nonce in sessionStorage
			sessionStorage.setItem('oauth_code_verifier', codeVerifier);
			sessionStorage.setItem('oauth_nonce', nonce);

			// Build authorization URL
			const authUrl = new URL('authorize', process.env.GATSBY_ACCOUNTS_FRONTEND_URL);
			authUrl.searchParams.set('response_type', 'code');
			authUrl.searchParams.set('client_id', process.env.GATSBY_OAUTH_CLIENT_ID);
			authUrl.searchParams.set('redirect_uri', OAuthCode._makeRedirectUri());
			authUrl.searchParams.set('scope', 'school_admin teacher');
			authUrl.searchParams.set('state', base64URLEncodeString(JSON.stringify(state)));
			authUrl.searchParams.set('code_challenge', codeChallenge);
			authUrl.searchParams.set('code_challenge_method', 'S256');
			authUrl.searchParams.set('locale', locale);

			return authUrl.toString();
		} catch (error) {
			console.error('Failed to initiate OAuth flow:', error);
			// Clean up on error
			sessionStorage.removeItem('oauth_code_verifier');
			sessionStorage.removeItem('oauth_nonce');
			throw error;
		}
	},
	/**
	 * This decodes the base64 URL encoded state and returns the parsed object.
	 * @private
	 *
	 * @param {string} state
	 * @returns {Object} Parsed state object
	 */
	_parseState(state) {
		return JSON.parse(base64URLDecode(state));
	},
	/**
	 * Clean up OAuth-related data from sessionStorage
	 * @private
	 */
	_cleanupOAuthStorage() {
		sessionStorage.removeItem('oauth_nonce');
		sessionStorage.removeItem('oauth_code_verifier');
	},
	/**
	 * Remove OAuth parameters from URL without page reload
	 * @private
	 */
	_cleanupURL() {
		const url = new URL(window.location);
		url.searchParams.delete('code');
		url.searchParams.delete('state');

		// Update URL without reloading the page
		window.history.replaceState({}, document.title, url.toString());
	},
	/**
	 * Exchange OAuth authorization code for tokens using PKCE
	 *
	 * @param callbackData OAuth callback data containing code and verifier
	 * @returns The response from the OAuth token endpoint
	 * @throws Error if the exchange fails or parameters are missing
	 */
	exchangeCodeForTokens: async () => {
		try {
			const code = new URLSearchParams(window.location.search).get('code');
			const state = new URLSearchParams(window.location.search).get('state');

			if (!code || !state) {
				throw new Error('Missing required OAuth callback parameters: code and state');
			}

			// Parse state parameter to get nonce and locale
			const parsedState = OAuthCode._parseState(state);

			// Verify state parameter matches stored nonce
			const storedNonce = sessionStorage.getItem('oauth_nonce');
			if (!storedNonce || parsedState.nonce !== storedNonce) {
				throw new Error('Invalid state parameter - possible CSRF attack');
			}

			// Get stored PKCE verifier
			const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
			if (!codeVerifier) {
				throw new Error('Missing code verifier - OAuth flow corrupted');
			}

			const params = new URLSearchParams();
			params.append('grant_type', 'authorization_code');
			params.append('client_id', process.env.GATSBY_OAUTH_CLIENT_ID);
			params.append('redirect_uri', OAuthCode._makeRedirectUri());
			params.append('code', code);
			params.append('code_verifier', codeVerifier);

			const response = await fetch(`${process.env.GATSBY_ACCOUNTS_API_URL}/oauth/token`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: params.toString(),
			});

			// Clean up stored OAuth data
			OAuthCode._cleanupOAuthStorage();

			// Clean up URL parameters
			OAuthCode._cleanupURL();

			return { response, locale: parsedState.locale };
		} catch (error) {
			const state = new URLSearchParams(window.location.search).get('state');
			const { loginOriginUrl } = OAuthCode._parseState(state);
			sessionStorage.setItem(loginOriginKey, loginOriginUrl);

			// Clean up on error
			OAuthCode._cleanupOAuthStorage();
			console.error('OAuth callback processing failed:', error);
			throw error;
		}
	},
	/**
	 * Get the login origin URL stored in the OAuth state. This URL is used to redirect the user back to the original page
	 * they were on before starting the authentication process if login fails. The URL will only be available after
	 * [exchangeCodeForTokens] fails.
	 * @returns {string} The login origin URL or '/' if not found.
	 */
	getLoginOriginUrl: () => {
		const loginOriginUrl = sessionStorage.getItem(loginOriginKey);
		return loginOriginUrl || '/';
	},
};
